import { z } from 'zod';

/** Separate from fast/full graph construction; selected before any tool runs. */
export const EvidenceProfileSchema = z.enum([
  'suppression-audit-v1',
  'ruff-check-v1',
]);
export type EvidenceProfile = z.infer<typeof EvidenceProfileSchema>;

export const SUPPRESSION_FILE_POLICY = 'git-tracked-source-v1';
export const SUPPRESSION_SOURCE_BYTES = 1_000_000;
export const SUPPRESSION_TOTAL_BYTES = 128 * 1024 * 1024;
export const SUPPRESSION_FILE_BUDGET = 100_000;
// Ruff materializes inputs on disk. These are independent of suppression reads.
export const RUFF_SOURCE_BYTES = 1_000_000;
export const RUFF_TOTAL_BYTES = 32 * 1024 * 1024;
export const RUFF_FILE_BUDGET = 10_000;
export const SUPPRESSION_EXTENSIONS = [
  '.cjs',
  '.css',
  '.cts',
  '.go',
  '.js',
  '.json',
  '.jsonc',
  '.jsx',
  '.mjs',
  '.mts',
  '.py',
  '.pyi',
  '.rs',
  '.scss',
  '.svelte',
  '.toml',
  '.ts',
  '.tsx',
  '.vue',
  '.yaml',
  '.yml',
] as const;

/** Counts describe sources, never estimates of findings in unread sources. */
export const SourceCoverageSchema = z
  .object({
    policy: z.enum([SUPPRESSION_FILE_POLICY, 'git-tracked-python-v1']),
    enumerated: z.number().int().nonnegative(),
    eligible: z.number().int().nonnegative(),
    read: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    unreadable: z.number().int().nonnegative(),
    oversized: z.number().int().nonnegative(),
    unsafe: z.number().int().nonnegative(),
    unexamined: z.number().int().nonnegative(),
    bytesRead: z.number().int().nonnegative().max(SUPPRESSION_TOTAL_BYTES),
    /** Successful bytes plus a full read reservation for each failed read. */
    readBudgetUsed: z.number().int().nonnegative().max(SUPPRESSION_TOTAL_BYTES),
    enumerationComplete: z.boolean(),
    budgetExhausted: z.boolean(),
    complete: z.boolean(),
    unknownFindings: z.literal(0).nullable(),
  })
  .superRefine((coverage, context) => {
    const complete =
      coverage.enumerationComplete &&
      !coverage.budgetExhausted &&
      coverage.unreadable +
        coverage.oversized +
        coverage.unsafe +
        coverage.unexamined ===
        0;
    if (
      coverage.complete !== complete ||
      coverage.bytesRead > coverage.readBudgetUsed ||
      coverage.unknownFindings !== (complete ? 0 : null) ||
      coverage.enumerated !==
        coverage.eligible + coverage.excluded + coverage.unsafe ||
      coverage.eligible !==
        coverage.read +
          coverage.unreadable +
          coverage.oversized +
          coverage.unexamined
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Inconsistent source coverage',
      });
    }
  })
  .refine(
    (coverage) =>
      coverage.policy !== 'git-tracked-python-v1' ||
      (coverage.readBudgetUsed <= RUFF_TOTAL_BYTES &&
        coverage.read <= RUFF_FILE_BUDGET),
    { message: 'Ruff source coverage exceeds materialization budget' },
  );
export type SourceCoverage = z.infer<typeof SourceCoverageSchema>;
